import React, { useMemo } from "react";
import { connect } from "react-redux";
import objectPath from "object-path";
import Header from "./header/Header";
import SubHeader from "./sub-header/SubHeader";
import HeaderMobile from "./header/HeaderMobile";
import AsideLeft from "./aside/AsideLeft";
import Footer from "./footer/Footer";
import ScrollTop from "../../app/partials/layout/ScrollTop";
import HTMLClassService from "./HTMLClassService";
import LayoutConfig from "./LayoutConfig";
import MenuConfig from "./MenuConfig";
import MenuConfigEmbarcador from "./MenuConfigEmbarcador";
import MenuConfigUsina from "./MenuConfigUsina";
import { hasPermission } from "../../app/utils/Utils";
import LayoutInitializer from "./LayoutInitializer";
import KtContent from "./KtContent";
import "./assets/Base.scss";

const htmlClassService = new HTMLClassService();

const usinaUserIds = new Set([
  990, 1760, 1793, 1038, 949, 1187, 1807, 1752, 1810, 1659, 1074, 1663,
  1382, 1483, 1067, 1820, 968, 1735, 1862, 1736, 1867, 1269, 1860, 1569,
  1890, 1894, 1872, 1909, 1911, 1916, 1918, 1922, 1923, 1926, 1912, 1962,
  1964, 1851, 1980, 1989, 1990, 1988, 2011, 1838, 2012, 2010, 2017, 2018,
  2019, 2015, 1808, 2014, 2016, 2021, 2024, 2020, 2025, 2022, 1837, 2026,
  2027, 2031, 2032, 2036, 2034, 2048, 2082, 2085, 2236, 2252,
]);

function filtrarMenuArmazem(config, isArmazem) {
  const clone = JSON.parse(JSON.stringify(config));
  clone.aside.items = clone.aside.items
    .map((item) => {
      if (!item.submenu) return item;
      item.submenu = item.submenu.filter((sub) => !sub.armazem || isArmazem);
      return item;
    })
    .filter((item) => !item.submenu || item.submenu.length > 0);
  return clone;
}

const MenuConfigEmbarcadorArmazem = filtrarMenuArmazem(MenuConfigEmbarcador, true);
const MenuConfigEmbarcadorPadrao = filtrarMenuArmazem(MenuConfigEmbarcador, false);

function usaMenuLegadoDeUsina(user) {
  return user?.token_type !== "usina" && usinaUserIds.has(user?.id);
}

function getMenuConfig(user) {
  if (!user) {
    return MenuConfigEmbarcadorPadrao;
  }

  if (user.token_type === "usina" || usinaUserIds.has(user.id)) {
    return MenuConfigUsina;
  }

  if (user.token_type === "transportadora") {
    return MenuConfig;
  }

  return user?.empresa?.emp_flag_embarcador_armazem === 1
    ? MenuConfigEmbarcadorArmazem
    : MenuConfigEmbarcadorPadrao;
}

function filtrarPorPermissao(config, permissions) {
  const filtrarItens = (itens) =>
    itens.reduce((acumulado, item) => {
      if (item.permission && !hasPermission(permissions, item.permission)) {
        return acumulado;
      }

      if (item.submenu) {
        const submenuFiltrado = filtrarItens(item.submenu);

        if (submenuFiltrado.length > 0) {
          acumulado.push({ ...item, submenu: submenuFiltrado });
        }

        return acumulado;
      }

      acumulado.push(item);
      return acumulado;
    }, []);

  return {
    ...config,
    aside: { ...config.aside, items: filtrarItens(config.aside.items) },
  };
}

function Layout({
  user,
  children,
  asideDisplay,
  subheaderDisplay,
  selfLayout,
  layoutConfig,
  contentContainerClasses,
  permissions,
}) {
  htmlClassService.setConfig(layoutConfig);
  // scroll to top after location changes
  // window.scrollTo(0, 0);

  const contentCssClasses = htmlClassService.classes.content.join(" ");

  const menuConfig = useMemo(() => {
    const config = getMenuConfig(user);

    return usaMenuLegadoDeUsina(user)
      ? config
      : filtrarPorPermissao(config, permissions);
  }, [user, permissions]);

  return selfLayout !== "blank" ? (
    <LayoutInitializer
      menuConfig={menuConfig}
      layoutConfig={LayoutConfig}
      htmlClassService={htmlClassService}
    >
      {/* <!-- begin:: Header Mobile --> */}
      <HeaderMobile />
      {/* <!-- end:: Header Mobile --> */}

      <div className="kt-grid kt-grid--hor kt-grid--root">
        {/* <!-- begin::Body --> */}
        <div className="kt-grid__item kt-grid__item--fluid kt-grid kt-grid--ver kt-page">
          {/* <!-- begin:: Aside Left --> */}
          {asideDisplay && (
            <>
              <AsideLeft />
            </>
          )}
          {/* <!-- end:: Aside Left --> */}
          <div
            className="kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor kt-wrapper"
            id="kt_wrapper"
          >
            {/* <!-- begin:: Header READY --> */}

            <Header />
            {/* <!-- end:: Header --> */}

            {/* <!-- begin:: Content --> */}
            <div
              id="kt_content"
              className={`kt-content ${contentCssClasses} kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor`}
            >
              {/* <!-- begin:: Content Head --> */}
              {subheaderDisplay && <SubHeader />}
              {/* <!-- end:: Content Head --> */}

              {/* <!-- begin:: Content Body --> */}
              {/* TODO: add class to animate  kt-grid--animateContent-finished */}
              <KtContent>{children}</KtContent>
              {/*<!-- end:: Content Body -->*/}
            </div>
            {/* <!-- end:: Content --> */}
            <Footer />
          </div>
        </div>
        {/* <!-- end:: Body --> */}
      </div>
      <ScrollTop />
    </LayoutInitializer>
  ) : (
    // BLANK LAYOUT
    <div className="kt-grid kt-grid--ver kt-grid--root">
      <KtContent>{children}</KtContent>
    </div>
  );
}

const mapStateToProps = ({ auth: { user, permissions }, builder: { layoutConfig } }) => ({
  user,
  permissions,
  layoutConfig,
  selfLayout: objectPath.get(layoutConfig, "self.layout"),
  asideDisplay: objectPath.get(layoutConfig, "aside.self.display"),
  subheaderDisplay: objectPath.get(layoutConfig, "subheader.display"),
  desktopHeaderDisplay: objectPath.get(
    layoutConfig,
    "header.self.fixed.desktop"
  ),
  contentContainerClasses: "",
  // contentContainerClasses: builder.selectors.getClasses(store, {
  //   path: "content_container",
  //   toString: true
  // })
});

export default connect(mapStateToProps)(Layout);
